<template>
  <article :class="classes" class="buy-now">
    <div
      class="wrap px-x-gutter-lg py-header-height md:px-x-gutter overflow-hidden"
    >
      <div class="buy-now__hero">
        <div class="buy-now__window mx-x-gutter">
          <window-frame class="buy-now__window-frame">
            <div
              class="flex flex-col items-center justify-center text-center min-h-win-h-1/2"
            >
              <header class="w-full px-x-gutter py-y-gutter-sm md:py-y-gutter">
                <!-- eslint-disable vue/no-v-html -->
                <h1
                  v-if="titleHtml"
                  class="heading lowercase"
                  v-html="titleHtml"
                />
                <!-- eslint-enable vue/no-v-html -->
                <h2 v-if="description" class="heading--sm mt-3/4em">
                  <sanity-blocks rich-text :blocks="description" />
                </h2>
                <h3 v-if="discountExplainer" class="mt-em leading-snug">
                  {{ discountExplainer }}
                </h3>
                <div class="mt-y-gutter-sm">
                  <cta
                    v-if="cta"
                    class="normal-case"
                    fill
                    @click.prevent="scrollToMain"
                  >
                    {{ cta }}
                  </cta>
                </div>
              </header>
            </div>
          </window-frame>
          <div>
            <div
              v-for="(fruit, i) in fruits"
              :key="`fruit-${fruit}`"
              :class="`buy-now__fruit--${fruit}`"
              class="buy-now__fruit"
            >
              <float
                ref="fruit"
                :offset="i % 2 ? '6%' : '-10%'"
                :speed="i % 2 ? 6 : 10"
                :reverse="i % 2 ? true : false"
                class="buy-now__floating-fruit"
              />
            </div>
          </div>
          <float
            v-if="floatingImage"
            offset="6%"
            speed="6"
            no-ratio
            class="buy-now__floating-image buy-now__floating-image-top-right"
          >
            <sanity-media :image="floatingImage" />
          </float>
          <float
            v-if="floatingImageBottomLeft"
            offset="6%"
            speed="6"
            no-ratio
            class="buy-now__floating-image buy-now__floating-image-bottom-left"
          >
            <sanity-media :image="floatingImageBottomLeft" />
          </float>
          <float
            v-if="floatingImageBottomRight"
            offset="6%"
            speed="6"
            no-ratio
            class="buy-now__floating-image buy-now__floating-image-bottom-right"
          >
            <sanity-media :image="floatingImageBottomRight" />
          </float>
        </div>
      </div>
    </div>
    <page-section
      v-if="whyItsGreat && whyItsGreat.length > 0"
      :title="whyItsGreatTitle"
      class="bg-pink text-cloud-burst mt-header-height"
    >
      <gradient
        :colors="[getTransparentColor('pink'), getColor('pink')]"
        force-gradient
        class="color-section__gradient color-section__gradient--top"
      />
      <template v-if="bp">
        <div
          v-if="bp.sm"
          class="wrap flex flex-wrap justify-center text-center"
        >
          <figure
            v-for="(reason, i) in whyItsGreat"
            :key="`reason-${i}`"
            class="flex flex-col flex-grow-0 flex-shrink-0 items-center w-1/4 min-w-64 px-6 pb-y-gutter-sm"
          >
            <sanity-media
              v-if="reason.thumbnail"
              :image="reason.thumbnail"
              no-ratio
            />
            <figcaption v-if="reason.heading1 || reason.heading2">
              <h3
                v-if="reason.heading1"
                class="heading--xs font-medium uppercase"
              >
                {{ reason.heading1 }}
              </h3>
              <h4 v-if="reason.heading2" class="leading-snug mt-3/4em">
                {{ reason.heading2 }}
              </h4>
            </figcaption>
          </figure>
        </div>
        <gallery
          v-else
          :slides="whyItsGreat"
          :options="{
            autoplay: {
              delay: 3000
            },
            effect: 'fade',
            fadeEffect: {
              crossFade: true
            }
          }"
          class="wrap text-center"
        >
          <figure
            v-for="(reason, i) in whyItsGreat"
            :key="`reason-${i}`"
            class="swiper-slide pb-y-gutter-sm"
          >
            <sanity-media
              v-if="reason.thumbnail"
              :image="reason.thumbnail"
              no-ratio
              class="max-w-3/4 mx-auto"
            />
            <figcaption v-if="reason.heading1 || reason.heading2">
              <h3
                v-if="reason.heading1"
                class="heading--xs font-medium uppercase"
              >
                {{ reason.heading1 }}
              </h3>
              <h4 v-if="reason.heading2" class="mt-1/2em">
                {{ reason.heading2 }}
              </h4>
            </figcaption>
          </figure>
        </gallery>
      </template>
    </page-section>
    <div id="buy-now-main">
      <page-section
        v-if="frequency && frequency.intervals.length > 0"
        :title="frequency.title"
        class="bg-peach text-cloud-burst"
      >
        <gradient
          :colors="[getTransparentColor(getColor('peach')), getColor('peach')]"
          force-gradient
          class="color-section__gradient color-section__gradient--top"
        />
        <div class="wrap text-center">
          <sanity-blocks
            v-if="frequency.text && frequency.text.length > 0"
            :blocks="frequency.text"
            rich-text
            class="text-lg md:text-xl lg:text-2xl"
          />
          <div
            class="flex flex-wrap justify-center text-center md:text-lg lg:text-xl"
          >
            <div
              v-for="(interval, i) in frequency.intervals"
              :key="`interval-${i}`"
              class="flex-grow-0 flex-shrink-1 w-full my-4 xs:my-6 sm:w-auto sm:mx-4 lg:mx-6 xl:mx-8"
            >
              <h5 v-if="frequency.headings" class="font-medium mb-em">
                {{ frequency.headings }}
              </h5>
              <circular-cta
                :aria-checked="String(model.activeInterval === interval)"
                class="w-3/4 sm:w-auto"
                :style="`--cta-background: ${getColor('peach')}`"
                pill
                @click.prevent="setFrequency(interval)"
              >
                <span class="whitespace-no-wrap">{{ interval }}</span>
              </circular-cta>
            </div>
          </div>
        </div>
      </page-section>
      <page-section
        v-if="filteredProducts.length > 0"
        title="choose your flavors"
        class="bg-apricot text-cloud-burst pb-0 z-above mb-y-gutter"
      >
        <gradient
          :colors="[
            getTransparentColor(getColor('apricot')),
            getColor('apricot')
          ]"
          force-gradient
          class="color-section__gradient color-section__gradient--top"
        />
        <variant-grid
          :products="filteredProducts"
          :interval="
            model.activeInterval ? `${model.activeInterval}` : undefined
          "
          :marquees="marquees"
          class="relative z-above"
        />
        <gradient
          :colors="[
            getColor('apricot'),
            getTransparentColor(getColor('apricot'))
          ]"
          force-gradient
          class="color-section__gradient color-section__gradient--bottom"
        />
      </page-section>
    </div>
    <page-section
      v-if="faq.faqs.length > 0"
      :title="faq.title"
      class="text-center pb-0"
    >
      <div
        v-for="(f, i) in faq.faqs"
        :key="`faq-${i}`"
        class="wrap mb-y-gutter xs:text-lg sm:text-xl md:text-2xl"
      >
        <sanity-blocks
          :blocks="f.question"
          rich-text
          class="font-medium uppercase"
        />
        <sanity-blocks :blocks="f.answer" rich-text class="mt-em" />
      </div>
    </page-section>
    <page-section class="text-center">
      <div class="container mb-y-gutter-sm">
        <div class="wrap">
          <div
            v-if="!nonCBDSite"
            class="mx-auto max-w-2/3 mb-y-gutter-sm md:max-w-1/2"
          >
            <wreath class="w-full" />
          </div>
          <h4 class="heading text-blue lowercase pb-y-gutter-sm">
            Feeling calm, <span class="inline-block">but focused</span>
            <div>gonna go <span class="inline-block">make something</span></div>
          </h4>
        </div>
      </div>
    </page-section>
  </article>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import imagesLoaded from 'imagesloaded'
import page from '~/mixins/page'
import sanityQuery from '~/sanity/queries/buy-now'
import preventOrphanedWord from '~/util/text/prevent-orphaned-word'
import VariantGrid from '~/components/shop/products/variant-grid.vue'
import Wreath from '~/components/wreath.vue'
import getCBDFilter from '~/util/get-cbd-filter'

const FRUITS = [
  'lime',
  'coconut',
  'cherry',
  'peach',
  'hibiscus',
  'orange',
  'blackberry'
]

export default {
  components: {
    VariantGrid,
    Wreath
  },

  mixins: [page],

  async asyncData({ route, params, query, store, $sanity, error }) {
    const slug = params.page
    const previewId = query.preview
    try {
      const buyNow = await $sanity.fetch(sanityQuery(slug, previewId))
      if (!buyNow) {
        throw new Error('Page not found')
      }
      return buyNow
    } catch (e) {
      return error({
        statusCode: 404,
        message: e.message
      })
    }
  },

  data() {
    return {
      nonCBDSite: process.env.nonCBDSite,
      isCBDSafe: getCBDFilter(process.env.nonCBDSite),
      fruitLoaded: false,
      discountExplainer: undefined,
      floatingImage: undefined,
      floatingImageBottomRight: undefined,
      floatingImageBottomLeft: undefined,
      cta: undefined,
      whyItsGreatTitle: "why it's great",
      whyItsGreat: [],
      frequency: {
        title: 'choose frequency',
        text: [],
        headings: 'delivery every',
        intervals: []
      },
      marquees: [],
      products: [],
      model: {
        activeInterval: undefined
      },
      faq: {
        title: undefined,
        faqs: []
      }
    }
  },

  computed: {
    ...mapState({
      headerHeight: state => state.screen.headerHeight
    }),
    ...mapGetters('screen', {
      bp: 'breakpoint'
    }),
    filteredProducts() {
      return this.products.filter(product => this.isCBDSafe(product.product))
    },
    classes() {
      return {
        'has-floating-image': !!this.floatingImage,
        'is-fruit-loaded': this.fruitLoaded
      }
    },
    titleHtml() {
      const title = this.title
      if (!title) {
        return
      }
      return preventOrphanedWord(title)
    },
    /**
     * Returns FRUITS without 'blackberry' if a floating image is defined
     */
    fruits() {
      return this.floatingImage
        ? FRUITS.filter(f => f !== 'blackberry')
        : FRUITS
    }
  },

  async mounted() {
    this.$nextTick(this.setInitialFrequency)
    if (this.$refs.fruit) {
      await new Promise(resolve =>
        imagesLoaded(this.$refs.fruit[0].$el, { background: true }, resolve)
      )
      this.fruitLoaded = true
    }
  },

  methods: {
    setInitialFrequency() {
      const intervals = this.frequency.intervals
      if (intervals && intervals.length > 0) {
        this.setFrequency(intervals[0])
      }
    },
    setFrequency(interval) {
      this.$set(this.model, 'activeInterval', interval)
    },
    scrollToMain() {
      const main = this.$el.querySelector('#buy-now-main')
      window.scrollTo({
        top: main.offsetTop - this.headerHeight,
        left: 0,
        behavior: 'smooth'
      })
    }
  }
}
</script>

<style lang="scss">
.page--shop-buy-now-page .page {
  padding-bottom: 0;
  padding-top: 0;
}

.buy-now__hero {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: calc(var(--win-height) - (2 * var(--header-height)));
}

.buy-now__window {
  flex: 0 1 auto;
  position: relative;
  width: 100%;
}

.buy-now__window-frame {
  position: relative;
  z-index: 2;
}

.buy-now__window .window__content {
  background-image: linear-gradient(
    theme('colors.peach'),
    theme('colors.pink'),
    theme('colors.melrose')
  );
}

.buy-now__fruit,
.buy-now__floating-image {
  opacity: 0;
  position: absolute;
  transform: scale(0.4);
  transition: opacity var(--fade-speed);
}

@for $i from 1 to (7) {
  .buy-now__fruit:nth-child(#{$i}) {
    transition-delay: #{($i - 1) * 0.05}s;
  }
}

.is-fruit-loaded .buy-now__fruit,
.buy-now__floating-image {
  opacity: 1;
}

.buy-now__floating-fruit {
  height: 100%;
  width: 100%;
}

.buy-now__floating-fruit::before,
.buy-now__floating-image .image {
  content: '';
  display: block;
  height: 100%;
  transform-origin: 50% 50%;
  width: 100%;
}

.buy-now__floating-fruit::before {
  background-image: url('~assets/images/buy-now-fruit.png');
  background-repeat: no-repeat;
  background-size: 750px 390px;
}

.buy-now__fruit--lime,
.buy-now__fruit--cherry,
.buy-now__fruit--peach,
.buy-now__fruit--hibiscus,
.buy-now__fruit--blackberry,
.buy-now__floating-image {
  z-index: 3;
}

.buy-now__fruit--lime {
  bottom: 0;
  height: 181px;
  right: 100%;
  transform-origin: 100% 100%;
  width: 153px;
}

.buy-now__fruit--lime .buy-now__floating-fruit::before {
  background-position: 0 -205px;
  transform: translateX(25%);
}

.buy-now__fruit--coconut {
  bottom: 75%;
  height: 170px;
  right: 100%;
  transform-origin: 100% 100%;
  width: 186px;
}

.buy-now__fruit--coconut .buy-now__floating-fruit::before {
  background-position: -386px -16px;
  transform: translate(50%, 25%);
}

.buy-now__fruit--cherry {
  bottom: 100%;
  height: 200px;
  left: 33.3%;
  transform-origin: 0 100%;
  width: 218px;
}

.buy-now__fruit--cherry .buy-now__floating-fruit::before {
  background-position: -164px -4px;
  transform: translate(-50%, 25%);
}

.buy-now__fruit--peach {
  bottom: 100%;
  height: 172px;
  right: 25%;
  transform-origin: 100% 100%;
  width: 190px;
}

.buy-now__fruit--peach .buy-now__floating-fruit::before {
  background-position: -320px -212px;
  transform: translate(50%, 50%);
}

.has-floating-image .buy-now__fruit--peach {
  height: 172px;
  left: 25%;
  top: 100%;
  transform-origin: 0 0;
  width: 190px;
}

.has-floating-image .buy-now__fruit--peach .buy-now__floating-fruit::before {
  background-position: -320px -212px;
  transform: translate(-50%, -50%);
}

.buy-now__fruit--hibiscus {
  height: 162px;
  left: 100%;
  top: 75%;
  transform-origin: 0 0;
  width: 172px;
}

.buy-now__fruit--hibiscus .buy-now__floating-fruit::before {
  background-position: -572px -20px;
  transform: translate(-50%, 25%);
}

.buy-now__fruit--orange {
  height: 180px;
  right: 33.3%;
  top: 100%;
  transform-origin: 100% 0;
  width: 162px;
}

.buy-now__fruit--orange .buy-now__floating-fruit::before {
  background-position: -156px -204px;
  transform: translate(50%, -25%);
}

.buy-now__fruit--blackberry {
  background-position: -2px -4px;
  height: 168px;
  left: 25%;
  top: 100%;
  transform-origin: 0 0;
  width: 158px;
}

.buy-now__fruit--blackberry .buy-now__floating-fruit::before {
  background-position: -2px -4px;
  transform: translate(-50%, -25%);
}

.buy-now__floating-image-top-right {
  bottom: 100%;
  right: 25%;
  width: 295px;
}

.buy-now__floating-image-bottom-left {
  bottom: 25%;
  left: -7%;
  width: 350px;
  z-index: 0;
}

.buy-now__floating-image-bottom-right {
  bottom: 10%;
  right: 15%;
  width: 220px;
  z-index: 0;
}

.buy-now__floating-image-top-right .image {
  transform: scale(0.4) translate(50%, 50%);
  transform-origin: 100% 100%;
}

.buy-now__floating-image-bottom-right .image {
  transform: scale(0.3) translate(210%, 70%);
}

.buy-now__floating-image-bottom-left .image {
  transform: scale(0.3) translate(-130%, 100%);
}

@screen xs {
  .is-fruit-loaded .buy-now__fruit {
    transform: scale(0.5);
  }

  .buy-now__floating-image-bottom-left {
    z-index: 0;
  }

  .buy-now__floating-image-bottom-right {
    z-index: 3;
  }

  .buy-now__floating-image-top-right .image {
    transform: scale(0.5) translate(50%, 50%);
  }

  .buy-now__floating-image-bottom-right .image {
    transform: scale(0.4) translate(130%, 180%);
  }

  .buy-now__floating-image-bottom-left .image {
    transform: scale(0.4) translate(-90%, 100%);
  }
}

@screen md {
  .buy-now__hero {
    min-height: var(--win-h-2\/3);
  }

  .is-fruit-loaded .buy-now__fruit {
    transform: scale(0.6);
  }

  .buy-now__floating-image-bottom-left {
    z-index: 3;
  }

  .buy-now__floating-image-top-right .image {
    transform: scale(0.6) translate(50%, 50%);
  }

  .buy-now__floating-image-bottom-right .image {
    transform: scale(0.5) translate(80%, 110%);
  }

  .buy-now__floating-image-bottom-left .image {
    transform: scale(0.5) translate(0%, 100%);
  }
}

@screen lg {
  .is-fruit-loaded .buy-now__fruit {
    transform: scale(0.8);
  }

  .buy-now__fruit--lime .buy-now__floating-fruit::before {
    transform: translateX(-20%);
  }

  .buy-now__floating-image-top-right .image {
    transform: scale(0.8) translate(50%, 50%);
  }

  .buy-now__floating-image-bottom-right .image {
    transform: scale(0.6) translate(50%, 90%);
  }

  .buy-now__floating-image-bottom-left .image {
    transform: scale(0.6) translate(10%, 50%);
  }
}

@screen xl {
  .is-fruit-loaded .buy-now__fruit {
    transform: scale(1);
  }

  .buy-now__fruit--orange .buy-now__floating-fruit::before {
    transform: translateY(-40%);
  }

  .buy-now__floating-image-top-right .image {
    transform: scale(0.9) translate(50%, 50%);
  }

  .buy-now__floating-image-bottom-right .image {
    transform: scale(0.9) translate(50%, 50%);
  }

  .buy-now__floating-image-bottom-left .image {
    transform: scale(0.9) translate(5%, 55%);
  }
}

@media (min-width: theme('screens.2xl')) {
  .buy-now__floating-image-bottom-left .image {
    transform: scale(0.9) translate(40%, 50%);
  }
}

@media (min-width: theme('screens.4xl')) {
  .buy-now__floating-image-bottom-left .image {
    transform: scale(0.9) translate(50%, 15%);
  }
}
</style>
